import { v4 as uuid } from "uuid";
import type { SyncStore as Store } from "../infra/state";
import { withState } from "../infra/state";
import type { MarlinStoreDb } from "../infra/db";
import { layoutOps } from "../screens/store";
import { recordTemplateLoad } from "../templates/store";
import type {
  CellData,
  GridId,
  GridMap,
  GridPosition,
  PinData,
  PositionData,
  ScreenLayout,
  ScreenLayoutTemplate,
  ScreenMethods,
} from "@observe-tech/utils";

type ScreenLayoutTemplateGridMethods = Pick<
  ScreenMethods,
  "setPositionData" | "setPinData"
>;

const createScreenLayoutTemplateOperations = (
  store: Store,
  gridMethods: ScreenLayoutTemplateGridMethods,
) => {
  const findScreenLayout = (screenId: number): ScreenLayout | null => {
    const currentLayouts = store.get("screenLayouts") || [];
    return currentLayouts.find((l) => l.id === screenId) || null;
  };

  const getScreenLayoutTemplates = (): ScreenLayoutTemplate[] => {
    return store.get("screenLayoutTemplates") || [];
  };

  const getSelectedScreenTemplates = (): Record<string, string | null> => {
    return store.get("selectedScreenTemplateIds") || {};
  };

  const setSelectedScreenTemplate = (
    templateId: string | null,
    screenId: number,
  ): void => {
    store.set("selectedScreenTemplateIds", {
      ...getSelectedScreenTemplates(),
      [screenId]: templateId,
    });
  };

  const saveScreenLayoutTemplate = (
    name: string,
    screenId: number,
  ): ScreenLayoutTemplate => {
    const screenLayout = findScreenLayout(screenId);
    if (!screenLayout) {
      throw new Error(`Screen layout with id ${screenId} not found`);
    }

    const templates = getScreenLayoutTemplates();

    const newTemplate: ScreenLayoutTemplate = {
      id: uuid(),
      name,
      createdAt: new Date(),
      screenLayout: structuredClone(screenLayout),
    };

    const updatedTemplates = [...templates, newTemplate];
    store.set("screenLayoutTemplates", updatedTemplates);
    setSelectedScreenTemplate(newTemplate.id, screenId);

    return newTemplate;
  };

  const deleteScreenLayoutTemplate = (templateId: string): boolean => {
    const templates = getScreenLayoutTemplates();
    const filteredTemplates = templates.filter((t) => t.id !== templateId);

    if (filteredTemplates.length === templates.length) {
      return false;
    }
    const selectedTemplates = getSelectedScreenTemplates();
    store.set(
      "selectedScreenTemplateIds",
      Object.fromEntries(
        Object.entries(selectedTemplates).map(([screenId, selectedId]) => [
          screenId,
          selectedId === templateId ? null : selectedId,
        ]),
      ),
    );

    store.set("screenLayoutTemplates", filteredTemplates);
    return true;
  };

  const getScreenLayoutTemplate = (
    templateId: string,
  ): ScreenLayoutTemplate | null => {
    const templates = getScreenLayoutTemplates();
    return templates.find((t) => t.id === templateId) || null;
  };

  const templateGrid = (screenLayout: ScreenLayout): GridMap => {
    return screenLayout.screenData.nCams === 1
      ? screenLayout.screenData.cameraGrid
      : screenLayout.screenData.cageGrid;
  };

  const inactiveGrid = (screenLayout: ScreenLayout): GridMap => {
    return screenLayout.screenData.nCams === 1
      ? screenLayout.screenData.cageGrid
      : screenLayout.screenData.cameraGrid;
  };
  const templateGridIds = (screenLayout: ScreenLayout): GridId[] => {
    const { rows, columns } = screenLayout.screenData.gridLayout;
    const gridIds: GridId[] = [];
    for (let row = 1; row <= rows; row++) {
      for (let column = 1; column <= columns; column++) {
        gridIds.push(`${row}-${column}` as GridId);
      }
    }
    return gridIds;
  };

  const gridIdToPosition = (gridId: GridId): GridPosition => {
    const [row, column] = gridId.split("-").map(Number);
    return { row, column } as GridPosition;
  };

  const cellToPositionData = (
    cell: NonNullable<CellData>,
    useCameraGrid: boolean,
  ): PositionData => {
    if (useCameraGrid) {
      return {
        machineId: cell.machineId,
        cageId: cell.cageId,
        cameraId: cell.cameras[0],
      };
    }
    return {
      machineId: cell.machineId,
      cageId: cell.cageId,
      cameras: cell.cameras,
    };
  };

  const applyScreenSettings = (
    template: ScreenLayoutTemplate,
    screenId: number,
  ): boolean => {
    const currentLayouts = store.get("screenLayouts") || [];
    const layoutIndex = currentLayouts.findIndex((l) => l.id === screenId);
    if (layoutIndex === -1) {
      return false;
    }

    const currentLayout = currentLayouts[layoutIndex];
    const { screenType, screenData } = template.screenLayout;

    currentLayouts[layoutIndex] = {
      ...currentLayout,
      screenType,
      screenData: {
        ...currentLayout.screenData,
        nCams: screenData.nCams,
        gridLayout: screenData.gridLayout,
      },
    };

    store.set("screenLayouts", currentLayouts);
    return true;
  };

  const applyScreenCells = (
    template: ScreenLayoutTemplate,
    screenId: number,
  ): void => {
    const { setPositionData } = gridMethods;
    const useCameraGrid = template.screenLayout.screenData.nCams === 1;
    const grid = templateGrid(template.screenLayout);

    templateGridIds(template.screenLayout).forEach((gridId) => {
      const cell = grid[gridId];
      setPositionData(
        screenId,
        gridIdToPosition(gridId),
        cell ? cellToPositionData(cell, useCameraGrid) : null,
      );
    });
  };
  const applyScreenPins = (
    template: ScreenLayoutTemplate,
    screenId: number,
  ): void => {
    const { setPinData } = gridMethods;
    const grid = templateGrid(template.screenLayout);

    templateGridIds(template.screenLayout).forEach((gridId) => {
      const cell = grid[gridId];
      if (!cell) {
        return;
      }
      setPinData(screenId, gridIdToPosition(gridId), cell.pin ?? null);
    });
  };
  const snapshotInactiveGridPins = (
    screenId: number,
  ): Record<string, PinData | undefined> => {
    const layout = findScreenLayout(screenId);
    if (!layout) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(inactiveGrid(layout))
        .filter(([, cell]) => cell)
        .map(([gridId, cell]) => [gridId, cell?.pin]),
    );
  };

  const applyFeedScreenData = (
    template: ScreenLayoutTemplate,
    screenId: number,
  ): void => {
    const currentLayouts = store.get("screenLayouts") || [];
    const layoutIndex = currentLayouts.findIndex((l) => l.id === screenId);
    if (layoutIndex === -1) {
      return;
    }

    currentLayouts[layoutIndex] = {
      ...currentLayouts[layoutIndex],
      feedScreenData: template.screenLayout.feedScreenData,
    };

    store.set("screenLayouts", currentLayouts);
  };
  const finalizeScreenLayout = (
    template: ScreenLayoutTemplate,
    screenId: number,
    inactiveGridPins: Record<string, PinData | undefined>,
  ): void => {
    const currentLayouts = store.get("screenLayouts") || [];
    const layoutIndex = currentLayouts.findIndex((l) => l.id === screenId);
    if (layoutIndex === -1) {
      return;
    }

    const layout = currentLayouts[layoutIndex];
    const restoredGrid = inactiveGrid(layout);
    Object.entries(inactiveGridPins).forEach(([gridId, pin]) => {
      const cell = restoredGrid[gridId as GridId];
      if (cell) {
        cell.pin = pin;
      }
    });

    const grid = templateGrid(layout);
    const gridIds = templateGridIds(layout);
    const keepOverlay = (overlayId: GridId | null): GridId | null => {
      if (!overlayId || !gridIds.includes(overlayId) || !grid[overlayId]) {
        return null;
      }
      return overlayId;
    };

    const { fullScreen, halfScreenLeft, halfScreenRight } =
      template.screenLayout.screenData;
    layout.screenData.fullScreen = keepOverlay(fullScreen);
    layout.screenData.halfScreenLeft = keepOverlay(halfScreenLeft);
    layout.screenData.halfScreenRight = keepOverlay(halfScreenRight);

    store.set("screenLayouts", currentLayouts);
  };

  const loadScreenLayoutTemplate = (
    templateId: string,
    screenId: number,
  ): boolean => {
    const template = getScreenLayoutTemplate(templateId);
    if (!template) {
      return false;
    }
    if (!applyScreenSettings(template, screenId)) return false;
    const inactiveGridPins = snapshotInactiveGridPins(screenId);
    applyScreenCells(template, screenId);
    applyScreenPins(template, screenId);
    applyFeedScreenData(template, screenId);
    finalizeScreenLayout(template, screenId, inactiveGridPins);
    setSelectedScreenTemplate(templateId, screenId);
    store.set("selectedMultiScreenTemplateId", null);
    return true;
  };

  return {
    getScreenLayoutTemplates,
    saveScreenLayoutTemplate,
    deleteScreenLayoutTemplate,
    getScreenLayoutTemplate,
    loadScreenLayoutTemplate,
    setSelectedScreenTemplate,
  };
};

const KEYS = [
  "screenLayouts",
  "machinesLoaded",
  "selected",
  "selectedMultiScreenTemplateId",
  "screenLayoutTemplates",
  "selectedScreenTemplateIds",
] as const;

const requireId = (templateId: string): void => {
  if (!templateId || typeof templateId !== "string")
    throw new Error("Template ID is required");
};

export const getScreenLayoutTemplates = (db: MarlinStoreDb) =>
  db.screenTemplates.toArray();

export const getScreenLayoutTemplate = async (
  db: MarlinStoreDb,
  templateId: string,
): Promise<ScreenLayoutTemplate> => {
  requireId(templateId);
  const template = await db.screenTemplates.get(templateId);
  if (!template) throw new Error("Template not found");
  return template;
};

export const saveScreenLayoutTemplate = (
  db: MarlinStoreDb,
  name: string,
  screenId: number,
): Promise<ScreenLayoutTemplate> => {
  if (!name || typeof name !== "string")
    throw new Error("Template name is required");
  return withState(db, KEYS, (store) =>
    createScreenLayoutTemplateOperations(
      store,
      layoutOps(store),
    ).saveScreenLayoutTemplate(name.trim(), screenId),
  );
};

export const deleteScreenLayoutTemplate = (
  db: MarlinStoreDb,
  templateId: string,
): Promise<{ success: true }> => {
  requireId(templateId);
  return withState(db, KEYS, (store) => {
    if (
      !createScreenLayoutTemplateOperations(
        store,
        layoutOps(store),
      ).deleteScreenLayoutTemplate(templateId)
    )
      throw new Error("Template not found");
    return { success: true as const };
  });
};

export const loadScreenLayoutTemplate = (
  db: MarlinStoreDb,
  templateId: string,
  screenId: number,
): Promise<{ success: true }> => {
  requireId(templateId);
  return withState(db, KEYS, async (store) => {
    const templates = createScreenLayoutTemplateOperations(
      store,
      layoutOps(store, { loadingScreenTemplateId: screenId }),
    );
    if (!templates.loadScreenLayoutTemplate(templateId, screenId))
      throw new Error("Template or screen not found");
    await recordTemplateLoad(db, templateId);
    return { success: true as const };
  });
};

export const getSelectedScreenTemplateId = async (
  db: MarlinStoreDb,
  screenId: number,
): Promise<string | null> =>
  (
    (await db.settings.get("selectedScreenTemplateIds"))?.value as
      | Record<string, string | null>
      | undefined
  )?.[screenId] ?? null;

export const setSelectedScreenTemplateId = (
  db: MarlinStoreDb,
  templateId: string | null,
  screenId: number,
): Promise<{ success: true }> => {
  if (templateId !== null && typeof templateId !== "string")
    throw new Error("Template ID must be a string or null");
  return withState(db, ["selectedScreenTemplateIds"], (store) => {
    store.set("selectedScreenTemplateIds", {
      ...store.get("selectedScreenTemplateIds"),
      [screenId]: templateId,
    });
    return { success: true as const };
  });
};
